/**
 * Production export — writes files to a directory via File System Access API
 * Falls back to JSZip download if API not available
 */
import { sanitizeFilename } from '@/utils/escape';
import { generateTokensCSS } from '../css/tokens';
import { generateComponentsCSS } from '../css/components';
import { generateMainJS } from '../js/main';
import { generateHTML } from '../html/page';
import { generateImpressumHTML } from '../html/legal/impressum';
import { generateDatenschutzHTML } from '../html/legal/datenschutz';
import { generateAgbHTML } from '../html/legal/agb';
import { generateAngebotHTML } from '../html/content-pages/angebot';
import { generateMethodenHTML } from '../html/content-pages/methoden';
import { generateUeberMichHTML } from '../html/content-pages/ueber-mich';
import { generateReadme } from './readme';
import { extractDataUrlAssets } from './extract-assets';

interface ExportFile {
  path: string;
  content: string | Uint8Array;
  binary?: boolean;
}

export async function exportProductionZip(
  components: any,
  order: string[],
  settings: any,
) {
  // Clone to avoid mutating live builder state
  const safeSettings = JSON.parse(JSON.stringify(settings || {}));
  const safeOrder = Array.isArray(order) ? [...order] : [];
  const safeComponents = JSON.parse(JSON.stringify(components || {}));

  // Assets extraction (data-URLs -> /assets)
  const assets = extractDataUrlAssets(safeComponents, safeSettings);

  // Collect all files as {path, content, binary} objects
  const files: ExportFile[] = [];

  // CSS files
  files.push({ path: 'css/tokens.css', content: generateTokensCSS(safeSettings) });
  files.push({ path: 'css/components.css', content: generateComponentsCSS() });

  // JS file
  files.push({ path: 'js/main.js', content: generateMainJS(safeSettings, safeComponents) });

  // Main HTML
  files.push({ path: 'index.html', content: generateHTML(safeComponents, safeOrder, safeSettings) });

  // Legal pages
  if (safeSettings.generateImpressum) {
    files.push({ path: 'impressum.html', content: generateImpressumHTML(safeSettings) });
  }
  if (safeSettings.generateDatenschutz) {
    files.push({ path: 'datenschutz.html', content: generateDatenschutzHTML(safeSettings) });
  }
  if (safeSettings.generateAgb) {
    files.push({ path: 'agb.html', content: generateAgbHTML(safeSettings) });
  }

  // Content pages
  if (safeSettings.generateAngebot) {
    files.push({ path: 'angebot.html', content: generateAngebotHTML(safeSettings, safeComponents) });
  }
  if (safeSettings.generateMethoden) {
    files.push({ path: 'methoden.html', content: generateMethodenHTML(safeSettings, safeComponents) });
  }
  if (safeSettings.generateUeberMich) {
    files.push({ path: 'ueber-mich.html', content: generateUeberMichHTML(safeSettings, safeComponents) });
  }

  // Backup JSON
  files.push({
    path: 'backup.json',
    content: JSON.stringify({
      generator: 'Studio',
      version: 'v5.0.0',
      exportedAt: new Date().toISOString(),
      settings: safeSettings,
      order: safeOrder,
      components: safeComponents,
    }, null, 2),
  });

  // README
  files.push({ path: 'README.txt', content: generateReadme(safeSettings, assets?.files?.length || 0) });

  // Asset files (binary)
  if (assets?.files?.length) {
    assets.files.forEach(function(f) {
      files.push({ path: 'assets/' + f.name, content: f.bytes, binary: true });
    });
  }

  try {
    // Primary: File System Access API (folder export)
    const dirHandle = await (window as any).showDirectoryPicker({ mode: 'readwrite' });

    // Helper: write a single file into the directory tree
    async function writeFile(rootHandle: any, filePath: string, content: string | Uint8Array, isBinary?: boolean) {
      const parts = filePath.split('/');
      const fileName = parts.pop()!;
      let parentHandle = rootHandle;

      // Create subdirectories as needed
      for (let i = 0; i < parts.length; i++) {
        parentHandle = await parentHandle.getDirectoryHandle(parts[i], { create: true });
      }

      // Create and write the file
      const fileHandle = await parentHandle.getFileHandle(fileName, { create: true });
      const writable = await fileHandle.createWritable();
      if (isBinary) {
        await writable.write(content);
      } else {
        await writable.write(new Blob([content as string], { type: 'text/plain;charset=utf-8' }));
      }
      await writable.close();
    }

    // Write all files into the selected folder
    for (let i = 0; i < files.length; i++) {
      await writeFile(dirHandle, files[i].path, files[i].content, !!files[i].binary);
    }

    return { success: true, fileCount: files.length };
  } catch (err: any) {
    if (err.name === 'AbortError') return { success: false, aborted: true };

    // Fallback: JSZip Download
    const JSZip = (await import('jszip')).default;
    const zip = new JSZip();

    for (const file of files) {
      if (file.binary) {
        zip.file(file.path, file.content as Uint8Array);
      } else {
        zip.file(file.path, file.content as string);
      }
    }

    const { saveAs } = await import('file-saver');
    const blob = await zip.generateAsync({ type: 'blob' });
    saveAs(blob, `${sanitizeFilename(settings.siteName)}-export.zip`);

    return { success: true, fileCount: files.length, method: 'zip' };
  }
}
